import { NodeFontSize } from './NodeFontSize';
import { isThemeColor, ThemeColor } from '@view-model/models/common/color';
import { TextAlign } from '@view-model/models/common/text-align';
import type { StickyNodeJSON } from 'balus-common';

type NodeStyles = {
  fillColor: string;
  borderColor: string;
  selection?: {
    backgroundColor?: string;
  };
};

const styleDefinitions: Record<ThemeColor, NodeStyles> = {
  [ThemeColor.White]: {
    fillColor: '#F5F5F5',
    borderColor: '#E0E0E0',
  },
  [ThemeColor.Red]: {
    fillColor: '#EF9A9A',
    borderColor: '#E57373',
  },
  [ThemeColor.Green]: {
    fillColor: '#C8E6C9',
    borderColor: '#A5D6A7',
  },
  [ThemeColor.Blue]: {
    fillColor: '#90CAF9',
    borderColor: '#64B5F6',
    selection: {
      // tailwindcssでは動的に色指定ができないため、直接tailwindcssのクラスをここに指定してclassNames()で結合する
      backgroundColor: 'selection:bg-gray-200',
    },
  },
  [ThemeColor.Orange]: {
    fillColor: '#FFCC80',
    borderColor: '#FFB74D',
  },
  [ThemeColor.Yellow]: {
    fillColor: '#FFF59D',
    borderColor: '#FFF176',
  },
  [ThemeColor.YellowGreen]: {
    fillColor: '#E6EE9C',
    borderColor: '#DCE775',
  },
  [ThemeColor.Purple]: {
    fillColor: '#E1BEE7',
    borderColor: '#CE93D8',
  },
};

type NodeStyleAttributes = {
  fontSize: NodeFontSize;
  fontStrikethrough: boolean;
  themeColor: ThemeColor;
  textAlign: TextAlign;
};

export class NodeStyle {
  readonly fontSize: NodeFontSize;
  readonly fontStrikethrough: boolean;
  readonly themeColor: ThemeColor;
  readonly textAlign: TextAlign;

  constructor(attributes: NodeStyleAttributes) {
    this.fontSize = attributes.fontSize;
    this.fontStrikethrough = attributes.fontStrikethrough;
    this.themeColor = attributes.themeColor;
    this.textAlign = attributes.textAlign;
  }

  static buildNew(themeColor?: ThemeColor): NodeStyle {
    return new this({
      fontSize: NodeFontSize.L,
      fontStrikethrough: false,
      themeColor: themeColor || ThemeColor.Red,
      textAlign: TextAlign.Center,
    });
  }

  static load(dump: StickyNodeJSON['style']): NodeStyle {
    const { fontSize, fontStrikethrough, themeColor, textAlign } = dump;

    return new this({
      fontSize: NodeFontSize.load(fontSize),
      fontStrikethrough: !!fontStrikethrough,
      themeColor: isThemeColor(themeColor) ? themeColor : ThemeColor.White,
      textAlign: TextAlign.load(textAlign),
    });
  }

  dump(): StickyNodeJSON['style'] {
    return {
      fontSize: NodeFontSize.dump(this.fontSize),
      fontStrikethrough: this.fontStrikethrough,
      themeColor: this.themeColor,
      textAlign: TextAlign.dump(this.textAlign),
    };
  }

  isEqual(other: NodeStyle): boolean {
    return (
      other instanceof NodeStyle &&
      this.fontSize === other.fontSize &&
      this.themeColor === other.themeColor &&
      this.fontStrikethrough === other.fontStrikethrough &&
      this.textAlign === other.textAlign
    );
  }

  private _cloneWith(attributes: Partial<NodeStyleAttributes>): NodeStyle {
    return new NodeStyle({
      fontSize: attributes.fontSize || this.fontSize,
      fontStrikethrough:
        attributes.fontStrikethrough === undefined ? this.fontStrikethrough : attributes.fontStrikethrough,
      themeColor: attributes.themeColor || this.themeColor,
      textAlign: attributes.textAlign || this.textAlign,
    });
  }

  getStyles(): NodeStyles {
    return styleDefinitions[this.themeColor];
  }

  withThemeColor(themeColor: ThemeColor): NodeStyle {
    return this._cloneWith({ themeColor });
  }

  withFontSize(fontSize: NodeFontSize): NodeStyle {
    return this._cloneWith({ fontSize });
  }

  withFontStrikethrough(fontStrikethrough: boolean): NodeStyle {
    return this._cloneWith({ fontStrikethrough });
  }

  withTextAlign(textAlign: TextAlign): NodeStyle {
    return this._cloneWith({ textAlign });
  }

  static getStyles(themeColor: ThemeColor): NodeStyles {
    return styleDefinitions[themeColor];
  }
}
